import React, { useEffect, useImperativeHandle, useState, useMemo } from 'react';
import {
    Box,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    Grid,
    Paper,
    FormControl,
    Checkbox,
} from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useDispatch, useSelector } from 'react-redux';
import { userPaymentMethodsSelector } from '../../redux/payment/selector';
import { createPaymentIntentForPurchase, getPaymentMethods } from '../../redux/payment/action';
import { IPaymentCard } from '../../types/global/generic.types';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { PaymentRequestButtonElement, PaymentElement } from '@stripe/react-stripe-js';
import { setPaymentMethod } from '../../redux/checkout/action';
import { checkoutSelectedPaymentMethodSelector } from '../../redux/checkout/selector';
import VisaCard from '../../components/CustomSVGIcons/VisaCard';
import MasterCard from '../../components/CustomSVGIcons/MasterCard';
import ApplePayIcon from '../../components/CustomSVGIcons/ApplePayIcon';
import GooglePayIcon from '../../components/CustomSVGIcons/GooglePayIcon';
import { getJWtDetails, showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import {
    AddressParam,
    PaymentIntent,
    PaymentRequestPaymentMethodEvent,
    StripePaymentElementChangeEvent,
} from '@stripe/stripe-js';
import { SentryCapture } from '../../analytics/Sentry';

interface PaymentDetails {
    saveCard?: boolean;
    email: string;
    address: AddressParam;
    details?: string;
    name?: string;
    phone?: string;
}
export interface IPaymentMethodSelectorRef {
    handleSubmit(
        currency: string,
        amount: number,
        details: PaymentDetails,
        metadata: Record<string, any>,
        callback: (...args: any) => void,
    ): Promise<void>;
}

export type IPType = 'saved_card' | 'new_card' | 'apple_pay' | 'google_pay';

export type PaymentMethodCallbackResult = PaymentIntent | { error?: { message?: string } };

export function isPaymentIntentResult(result: PaymentMethodCallbackResult): result is PaymentIntent {
    return Boolean(result && typeof result === 'object' && 'status' in result);
}

export interface ResolvePaymentClientSecretParams {
    currency: string;
    amount: number;
    saveCard: boolean;
    metadata: Record<string, unknown>;
}

export interface IPaymentMethodProps {
    onPaymentMethodSelected?: (type: IPType) => void;
    onChange?: (event: StripePaymentElementChangeEvent) => void;
    onSelectSavedCard?: (card: IPaymentCard) => void;
    onSaveForFutureChange?: (value: boolean) => void;
    preselectCard?: boolean;
    amount: number;
    details: PaymentDetails;
    currency: string;
    metadata: Record<string, any>;
    callBack: (paymentIntent: PaymentMethodCallbackResult, stripeEvent?: PaymentRequestPaymentMethodEvent) => void;
    preselectionRequired?: boolean;
    preselectionMessage?: string;
    prerequisite?: () => Promise<void>;
    /** Controlled: when provided with onSaveForFutureChange, parent controls save state (e.g. for Elements setup_future_usage) */
    saveForFuture?: boolean;
    /** When set, used instead of createPaymentIntentForPurchase (e.g. POST /campaign-wallet/fund). */
    resolvePaymentClientSecret?: (params: ResolvePaymentClientSecretParams) => Promise<string>;
    /** Parent renders save-card control (e.g. wallet fund above Elements). */
    hideSaveCardOption?: boolean;
    /** Keep Apple/Google Pay rows visible before amount is set; buttons stay disabled until ready. */
    digitalWalletsAlwaysVisible?: boolean;
    /** Disable card fields and wallet pay buttons (e.g. until wallet amount + fees are ready). */
    disablePaymentFields?: boolean;
}

export const PaymentMethodSelector = React.forwardRef<IPaymentMethodSelectorRef, IPaymentMethodProps>(
    (props: IPaymentMethodProps, ref) => {
        const { themeStyle } = useThemeContext();
        const {
            onChange,
            onPaymentMethodSelected,
            onSelectSavedCard,
            onSaveForFutureChange,
            preselectCard = false,
            amount,
            currency,
            details,
            metadata,
            callBack,
            preselectionRequired,
            preselectionMessage,
            saveForFuture: controlledSaveForFuture,
            resolvePaymentClientSecret,
            hideSaveCardOption = false,
            digitalWalletsAlwaysVisible = false,
            disablePaymentFields = false,
        } = props;
        const { isSignedIn } = getJWtDetails();
        const dispatch = useDispatch();
        const dispatchPromise = useDispatchPromise();
        const stripe = useStripe();
        const elements = useElements();
        const savedCards = useSelector(userPaymentMethodsSelector);
        const selectedPaymentMethod = useSelector(checkoutSelectedPaymentMethodSelector);
        const [paymentType, setPaymentType] = useState<string>(
            !isSignedIn ? 'new_card' : selectedPaymentMethod ? 'saved_card' : 'new_card',
        );
        const [selectedCard, setSelectedCard] = useState<string>(
            selectedPaymentMethod && selectedPaymentMethod !== 'new_card'
                ? (selectedPaymentMethod as IPaymentCard).payment_method
                : '',
        );
        const [saveForFutureUser, setSaveForFutureUse] = useState(details.saveCard ?? false);
        const saveForFuture = controlledSaveForFuture ?? saveForFutureUser;
        const setSaveForFuture = (value: boolean) => {
            if (onSaveForFutureChange) {
                onSaveForFutureChange(value);
            } else {
                setSaveForFutureUse(value);
            }
        };
        const [showAllCards, setShowAllCards] = useState(false);

        // Filter out expired cards
        const validSavedCards = useMemo(() => {
            if (!savedCards) return [];

            const currentDate = new Date();
            const currentYear = currentDate.getFullYear();
            const currentMonth = currentDate.getMonth() + 1; // JavaScript months are 0-indexed

            return savedCards.filter(card => {
                // Parse expiration dates as numbers
                const expYear = Number(card.authorization.exp_year);
                const expMonth = Number(card.authorization.exp_month);
                const paymentIntent = !!card.payment_intent;
                const paymentMethod = !!card.payment_method;

                // Card is not expired if:
                // 1. The expiration year is in the future, OR
                // 2. The expiration year is the current year AND the month is current or in the future
                return (
                    (paymentIntent && paymentMethod && expYear > currentYear) ||
                    (paymentIntent && paymentMethod && expYear === currentYear && expMonth >= currentMonth)
                );
            });
        }, [savedCards]);

        const [canMakePayment, setCanMakePayment] = useState({
            applePay: false,
            googlePay: false,
        });
        const [applePayPaymentRequest, setApplePayPaymentRequest] = useState<any | null>(null);
        const [googlePayPaymentRequest, setGooglePayPaymentRequest] = useState<any | null>(null);

        const handleOnSubmit = async (
            currency: string,
            amount: number,
            details: PaymentDetails,
            metadata: Record<string, any>,
            callBack: (...args: any) => void,
            stripeEvent?: PaymentRequestPaymentMethodEvent,
        ) => {
            try {
                if (!stripe) {
                    return;
                }

                const secret: string = resolvePaymentClientSecret
                    ? await resolvePaymentClientSecret({
                          currency,
                          amount,
                          saveCard: saveForFuture,
                          metadata,
                      })
                    : ((await dispatchPromise(
                          createPaymentIntentForPurchase({
                              currency: currency,
                              payment_method_type: 'card',
                              amount: amount,
                              save_card: saveForFuture,
                              metadata: JSON.stringify(metadata),
                          }),
                      )) as string);

                // Check if this is a Payment Request API call (Apple Pay/Google Pay) first
                // This takes precedence over the paymentType state
                if (stripeEvent) {
                    // For Payment Request API (Apple Pay/Google Pay), use confirmCardPayment
                    const { paymentIntent, error } = await stripe.confirmCardPayment(
                        secret,
                        { payment_method: stripeEvent.paymentMethod.id },
                        { handleActions: false },
                    );

                    // handle 3DS if needed
                    if (paymentIntent?.status === 'requires_action') {
                        const { error } = await stripe.confirmCardPayment(secret, {
                            payment_method: stripeEvent.paymentMethod.id,
                        });
                        if (error) {
                            return callBack(error, stripeEvent);
                        } else {
                            return callBack(paymentIntent, stripeEvent);
                        }
                    }

                    if (error) {
                        showToastMessage(error.message, 'error');
                        stripeEvent.complete('fail');
                        return callBack(error, stripeEvent);
                    }
                    stripeEvent.complete('success');
                    return callBack(paymentIntent, stripeEvent);
                }

                // For other payment methods, we need elements
                if (!elements) {
                    return;
                }

                if (paymentType === 'saved_card' && selectedCard) {
                    // For saved cards, use the payment_method directly
                    const card = validSavedCards.find(c => c._id === selectedCard);
                    if (card) {
                        const stripePaymentMethodId = card.payment_method;

                        // Confirm payment using the saved payment method
                        const { error, paymentIntent } = await stripe.confirmPayment({
                            clientSecret: secret,
                            confirmParams: {
                                return_url:
                                    window.location.origin +
                                    `?payment-complete=true&payment_intent_client_secret=${secret}`,
                                payment_method: stripePaymentMethodId,
                                receipt_email: details.email,
                            },
                            redirect: 'if_required',
                        });

                        if (error) {
                            showToastMessage(error.message, 'error');
                            callBack(error);
                            return;
                        }

                        callBack(paymentIntent);
                        return;
                    }
                }

                if (paymentType === 'new_card') {
                    // For new cards, use Elements
                    const { error: submitError } = await elements.submit();
                    if (submitError) {
                        showToastMessage(submitError.message, 'error');
                        return;
                    }

                    const { error, paymentIntent } = await stripe.confirmPayment({
                        elements,
                        confirmParams: {
                            return_url:
                                window.location.origin +
                                `?payment-complete=true&payment_intent_client_secret=${secret}`,
                            receipt_email: details.email,
                            payment_method_data: {
                                billing_details: {
                                    address: details.address,
                                    email: details.email,
                                    name: details.name,
                                    phone: details.phone,
                                },
                            },
                        },
                        clientSecret: secret,
                        redirect: 'if_required',
                    });

                    if (error) {
                        /** Handle Error here */
                        showToastMessage(error.message, 'error');
                        callBack(error);
                        return;
                    }

                    callBack(paymentIntent);
                }

                // If we get here and paymentType is apple_pay or google_pay, something went wrong
                if (paymentType === 'apple_pay' || paymentType === 'google_pay') {
                    return;
                }
            } catch (error) {
                console.log(error);
                SentryCapture(error);
                if (stripeEvent) {
                    stripeEvent.complete('fail');
                    showToastMessage('Failed to create payment intent. Please try again.', 'error');
                }
            }
        };

        const handlePaymentMethodReceived = async (event: PaymentRequestPaymentMethodEvent) => {
            try {
                // When Apple Pay/Google Pay is used, we know it's a digital wallet payment
                // so we can call handleOnSubmit directly - it will detect the stripeEvent
                // and use the correct payment flow
                await handleOnSubmit(currency, amount, details, metadata, callBack, event);
            } catch (error) {
                SentryCapture(error);
                showToastMessage('Failed to process payment method', 'error');
                event.complete('fail');
            }
        };

        useImperativeHandle(ref, () => ({
            handleSubmit: handleOnSubmit,
        }));

        const digitalWalletsReady = !preselectionRequired && amount > 0;
        const paymentRequestAmount = digitalWalletsAlwaysVisible
            ? Math.max(Math.round(amount), 100)
            : Math.round(amount);
        const shouldInitializeDigitalWallets =
            Boolean(stripe && currency) && (digitalWalletsAlwaysVisible || digitalWalletsReady);

        useEffect(() => {
            // Check if device supports Apple Pay or Google Pay
            if (!stripe || !currency || !shouldInitializeDigitalWallets) {
                if (!digitalWalletsAlwaysVisible) {
                    setApplePayPaymentRequest(null);
                    setGooglePayPaymentRequest(null);
                    setCanMakePayment({ applePay: false, googlePay: false });
                }
                return;
            }

            // Clear existing payment requests first
            setApplePayPaymentRequest(null);
            setGooglePayPaymentRequest(null);
            setCanMakePayment(prev => ({ ...prev, applePay: false, googlePay: false }));

            try {
                // Create payment request for Apple Pay
                const applePayPr = stripe.paymentRequest({
                    country: 'US',
                    currency: currency.toLowerCase(),
                    total: {
                        label: 'Total',
                        amount: paymentRequestAmount,
                    },
                    requestPayerName: true,
                    requestPayerEmail: true,
                });

                // Create payment request for Google Pay
                const googlePayPr = stripe.paymentRequest({
                    country: 'US',
                    currency: currency.toLowerCase(),
                    total: {
                        label: 'Total',
                        amount: paymentRequestAmount,
                    },
                    requestPayerName: true,
                    requestPayerEmail: true,
                });

                // Check Apple Pay availability
                applePayPr
                    .canMakePayment()
                    .then(result => {
                        console.log('Apple Pay canMakePayment result:', result);

                        if (result?.applePay) {
                            console.log('Setting up Apple Pay payment request event handlers');
                            applePayPr.on('paymentmethod', handlePaymentMethodReceived);
                            setApplePayPaymentRequest(applePayPr);
                            setCanMakePayment(prev => ({ ...prev, applePay: true }));
                        } else {
                            console.log('Apple Pay not available');
                            setApplePayPaymentRequest(null);
                            setCanMakePayment(prev => ({ ...prev, applePay: false }));
                        }
                    })
                    .catch(error => {
                        console.error('Error checking Apple Pay availability:', error);
                        SentryCapture(error);
                        setApplePayPaymentRequest(null);
                        setCanMakePayment(prev => ({ ...prev, applePay: false }));
                    });

                // Check Google Pay availability
                googlePayPr
                    .canMakePayment()
                    .then(result => {
                        console.log('Google Pay canMakePayment result:', result);

                        if (result?.googlePay) {
                            console.log('Setting up Google Pay payment request event handlers');
                            googlePayPr.on('paymentmethod', handlePaymentMethodReceived);
                            setGooglePayPaymentRequest(googlePayPr);
                            setCanMakePayment(prev => ({ ...prev, googlePay: true }));
                        } else {
                            console.log('Google Pay not available');
                            setGooglePayPaymentRequest(null);
                            setCanMakePayment(prev => ({ ...prev, googlePay: false }));
                        }
                    })
                    .catch(error => {
                        console.error('Error checking Google Pay availability:', error);
                        SentryCapture(error);
                        setGooglePayPaymentRequest(null);
                        setCanMakePayment(prev => ({ ...prev, googlePay: false }));
                    });
            } catch (error) {
                console.error('Error creating payment requests:', error);
                SentryCapture(error);
                setApplePayPaymentRequest(null);
                setGooglePayPaymentRequest(null);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
            stripe,
            currency,
            amount,
            paymentRequestAmount,
            shouldInitializeDigitalWallets,
            digitalWalletsAlwaysVisible,
        ]);

        // Cleanup effect to remove event listeners when payment requests change
        useEffect(() => {
            return () => {
                if (applePayPaymentRequest) {
                    applePayPaymentRequest.off('paymentmethod');
                }
                if (googlePayPaymentRequest) {
                    googlePayPaymentRequest.off('paymentmethod');
                }
            };
        }, [applePayPaymentRequest, googlePayPaymentRequest]);

        useEffect(() => {
            if (isSignedIn) {
                dispatch(getPaymentMethods());
            } else {
                // For non-signed-in users, always set default payment method to new_card
                dispatch(setPaymentMethod({ paymentMethod: 'new_card' }));
            }

            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [isSignedIn]);

        // Auto-select the first valid card when switching to saved_card type
        useEffect(() => {
            if (paymentType === 'saved_card' && validSavedCards.length > 0) {
                // If no card is selected or the selected card isn't in validSavedCards, select the first valid card
                const selectedCardExists = validSavedCards.find(card => card._id === selectedCard);
                if (!selectedCard || !selectedCardExists) {
                    setSelectedCard(validSavedCards[0]._id);
                    if (onSelectSavedCard) onSelectSavedCard(validSavedCards[0]);
                } else if (selectedCardExists && onSelectSavedCard) {
                    onSelectSavedCard(selectedCardExists);
                    setSelectedCard(selectedCardExists._id);
                }
                /** Enforce that we are using saved_card */
                onPaymentMethodSelected && onPaymentMethodSelected('saved_card');
            } else if (paymentType !== 'apple_pay' && paymentType !== 'google_pay') {
                onPaymentMethodSelected?.('new_card');
            }
        }, [paymentType, validSavedCards, selectedCard, preselectCard, onSelectSavedCard, onPaymentMethodSelected]);

        useEffect(() => {
            // When payment type or selected card changes, update the Redux store
            if (paymentType === 'saved_card' && selectedCard) {
                const card = validSavedCards.find(c => c._id === selectedCard);
                if (card) {
                    dispatch(setPaymentMethod({ paymentMethod: card }));
                }
            } else if (paymentType === 'new_card') {
                dispatch(setPaymentMethod({ paymentMethod: 'new_card' }));
            } else if (paymentType === 'apple_pay' || paymentType === 'google_pay') {
                // For digital wallets, set a special identifier
                dispatch(setPaymentMethod({ paymentMethod: { _id: paymentType } as any }));
            }
        }, [paymentType, selectedCard, dispatch, validSavedCards]);

        const handlePaymentTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            setPaymentType(event.target.value);
            onPaymentMethodSelected && onPaymentMethodSelected(event.target.value as IPType);
            onSelectSavedCard && onSelectSavedCard(validSavedCards[0]);
        };

        const handleCardSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
            setSelectedCard(event.target.value);
            const card = validSavedCards.find(card => card._id === event.target.value);
            if (card) {
                onSelectSavedCard && onSelectSavedCard(card);
            }
        };

        const renderCardIcon = (brand?: string) => {
            if (brand?.toLowerCase() === 'visa') {
                return <VisaCard width={30} height={30} />;
            } else if (brand?.toLowerCase() === 'master' || brand?.toLowerCase() === 'mastercard') {
                return <MasterCard width={30} height={30} />;
            }
            return null;
        };

        const renderCardInfo = (card: IPaymentCard) => {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {renderCardIcon(card.authorization.brand)}
                    <Box sx={{ ml: 1 }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                color: theme[themeStyle].primary_white,
                                fontSize: '0.9rem',
                            }}>
                            {card.authorization.brand} •••• {card.authorization.last4}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                color: theme[themeStyle].light_gray_text,
                                fontSize: '0.8rem',
                            }}>
                            Expires {card.authorization.exp_month}/{card.authorization.exp_year}
                        </Typography>
                    </Box>
                </Box>
            );
        };

        return (
            <Box>
                <RadioGroup value={paymentType} onChange={handlePaymentTypeChange}>
                    {/* Digital Wallets */}
                    {canMakePayment.applePay && (
                        <Paper
                            sx={{
                                mb: 2,
                                p: 2,
                                backgroundColor: theme[themeStyle].card_grey,
                                borderRadius: '8px',
                            }}>
                            <FormControlLabel
                                value="apple_pay"
                                disabled={!digitalWalletsAlwaysVisible && !digitalWalletsReady}
                                control={<Radio sx={{ color: theme[themeStyle].primary_white }} />}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <ApplePayIcon fill={theme[themeStyle].tag_white} />
                                        <Typography
                                            sx={{
                                                ml: 1,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                color: theme[themeStyle].primary_white,
                                            }}>
                                            Apple Pay
                                        </Typography>
                                    </Box>
                                }
                            />
                            {paymentType === 'apple_pay' && applePayPaymentRequest && canMakePayment.applePay && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        opacity: digitalWalletsReady && !disablePaymentFields ? 1 : 0.45,
                                        pointerEvents: digitalWalletsReady && !disablePaymentFields ? 'auto' : 'none',
                                    }}>
                                    <PaymentRequestButtonElement options={{ paymentRequest: applePayPaymentRequest }} />
                                    {!digitalWalletsReady && preselectionMessage ? (
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                color: theme[themeStyle].form_label,
                                                fontSize: 12,
                                                mt: 1,
                                            }}>
                                            {preselectionMessage}
                                        </Typography>
                                    ) : null}
                                </Box>
                            )}
                        </Paper>
                    )}

                    {canMakePayment.googlePay && (
                        <Paper
                            sx={{
                                mb: 2,
                                p: 2,
                                backgroundColor: theme[themeStyle].card_grey,
                                borderRadius: '8px',
                            }}>
                            <FormControlLabel
                                value="google_pay"
                                disabled={!digitalWalletsAlwaysVisible && !digitalWalletsReady}
                                control={<Radio sx={{ color: theme[themeStyle].primary_white }} />}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <GooglePayIcon fill={theme[themeStyle].tag_white} />
                                    </Box>
                                }
                            />
                            {paymentType === 'google_pay' && googlePayPaymentRequest && canMakePayment.googlePay && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        opacity: digitalWalletsReady && !disablePaymentFields ? 1 : 0.45,
                                        pointerEvents: digitalWalletsReady && !disablePaymentFields ? 'auto' : 'none',
                                    }}>
                                    <PaymentRequestButtonElement
                                        options={{ paymentRequest: googlePayPaymentRequest }}
                                    />
                                    {!digitalWalletsReady && preselectionMessage ? (
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                color: theme[themeStyle].form_label,
                                                fontSize: 12,
                                                mt: 1,
                                            }}>
                                            {preselectionMessage}
                                        </Typography>
                                    ) : null}
                                </Box>
                            )}
                        </Paper>
                    )}

                    {/* Saved Cards */}
                    {validSavedCards.length > 0 && isSignedIn && (
                        <Paper
                            sx={{
                                mb: 2,
                                p: 2,
                                backgroundColor: theme[themeStyle].card_grey,
                                borderRadius: '8px',
                            }}>
                            <FormControlLabel
                                value="saved_card"
                                control={
                                    <Radio
                                        sx={{
                                            color: theme[themeStyle].highlight,
                                            '&.Mui-checked': {
                                                color: theme[themeStyle].highlight, // Color for checked state
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].primary_white,
                                        }}>
                                        Saved Cards
                                    </Typography>
                                }
                            />

                            {paymentType === 'saved_card' && (
                                <Box sx={{ mt: 2 }}>
                                    <RadioGroup value={selectedCard} onChange={handleCardSelection}>
                                        <Grid container spacing={2}>
                                            {validSavedCards.slice(0, showAllCards ? undefined : 3).map(card => (
                                                <Grid item xs={12} key={card._id}>
                                                    <Paper
                                                        sx={{
                                                            p: 2,
                                                            backgroundColor:
                                                                selectedCard === card._id
                                                                    ? theme[themeStyle].deep_grey_lighter
                                                                    : theme[themeStyle].deep_grey,
                                                            borderRadius: '8px',
                                                            cursor: 'pointer',
                                                        }}
                                                        onClick={() => {
                                                            setSelectedCard(card._id);
                                                            onSelectSavedCard && onSelectSavedCard(card);
                                                        }}>
                                                        <FormControlLabel
                                                            value={card._id}
                                                            control={
                                                                <Radio
                                                                    sx={{
                                                                        color: theme[themeStyle].highlight,
                                                                        '&.Mui-checked': {
                                                                            color: theme[themeStyle].highlight, // Color for checked state
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            label={renderCardInfo(card)}
                                                        />
                                                    </Paper>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </RadioGroup>

                                    {/* Show More/Collapse Button */}
                                    {validSavedCards.length > 3 && (
                                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                                            <Typography
                                                onClick={() => setShowAllCards(!showAllCards)}
                                                sx={{
                                                    color: theme[themeStyle].highlight,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    fontSize: '0.9rem',
                                                    cursor: 'pointer',
                                                    textDecoration: 'underline',
                                                    '&:hover': {
                                                        opacity: 0.8,
                                                    },
                                                }}>
                                                {showAllCards
                                                    ? 'Collapse'
                                                    : `Show More (${validSavedCards.length - 3} more)`}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Paper>
                    )}

                    {/* New Card */}
                    <Paper
                        sx={{
                            mb: 2,
                            p: 2,
                            backgroundColor: theme[themeStyle].card_grey,
                            borderRadius: '8px',
                        }}>
                        <FormControlLabel
                            value="new_card"
                            control={
                                <Radio
                                    sx={{
                                        color: theme[themeStyle].highlight,
                                        '&.Mui-checked': {
                                            color: theme[themeStyle].highlight, // Color for checked state
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].primary_white,
                                    }}>
                                    Add Payment Method
                                </Typography>
                            }
                        />

                        {paymentType === 'new_card' && (
                            <Box
                                sx={{
                                    mt: 2,
                                    opacity: disablePaymentFields ? 0.5 : 1,
                                    pointerEvents: disablePaymentFields ? 'none' : 'auto',
                                }}>
                                <PaymentElement onChange={onChange} />
                                {!hideSaveCardOption && (
                                    <FormControl fullWidth>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={saveForFuture}
                                                    onChange={() => {
                                                        setSaveForFuture(!saveForFuture);
                                                    }}
                                                    sx={{
                                                        color: theme[themeStyle].gray_sidebar,
                                                        '&.Mui-checked': {
                                                            color: theme[themeStyle].highlight,
                                                        },
                                                        '&.MuiCheckbox-indeterminate': {
                                                            color: theme[themeStyle].highlight,
                                                        },
                                                    }}
                                                />
                                            }
                                            label={
                                                <Typography
                                                    sx={{
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        fontSize: 16,
                                                    }}>
                                                    Save card for future use.
                                                </Typography>
                                            }
                                        />
                                    </FormControl>
                                )}
                            </Box>
                        )}
                    </Paper>
                </RadioGroup>
            </Box>
        );
    },
);
